<style>

::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar-thumb {
    background: rgba(191, 188, 188, 0.6);
}

.required_err {
    color: rgba(255, 0, 0);
}

.elevation-1 tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, .03) !important;
}

.br {
    color: rgba(109, 103, 164);
}

.graph_vif_select {
    max-width: 620px;
    width: 50%;
}

.br_before_iface_graphs {
    height: 20px;
}

.br_iface_info {
    padding-bottom: 10px;
}

.br_iface_graph_section {
    margin-top: 20px;
}

.br_iface_graph {
    height: 340px;
    width: 100%;
}

@media (max-width: 900px) {
    .graph_vif_select {
        width: 100%;
    }
}

</style>

<template>
<div>
    <div>
        <vif_box
            v-model="show_vif_form"
            @canceled="on_vif_form_cancel"
            @selected="on_vif_form_select($event)"
        ></vif_box>

        <pppoe_sub_box
            v-model="show_pppoe_sub_form"
            @canceled="on_pppoe_sub_form_cancel"
            @selected="on_pppoe_sub_form_select($event)"
        ></pppoe_sub_box>

        <ipoe_sub_box
            v-model="show_ipoe_sub_form"
            @canceled="on_ipoe_sub_form_cancel"
            @selected="on_ipoe_sub_form_select($event)"
        ></ipoe_sub_box>
    </div>

    <h1 class="br">Interface graphs</h1>
    <ValidationObserver ref="nh_observer" v-slot="{ invalid }">
        <div class="graph_vif_select">
            <v-combobox
                v-model="iface_type"
                label="Interface type"
                :items="['VIF', 'PPPoE subscriber', 'IPoE subscriber']"
            ></v-combobox>

            <ValidationProvider rules="required|numeric" v-slot="{ errors }">
                <v-text-field
                    v-model="vif_id"
                    width="200px"
                    label="Interface ID"
                >
                    <template #append>
                        <v-btn @click="open_iface_form()">...</v-btn>
                    </template>
                </v-text-field>
                <span class="required_err">{{ errors[0] }}</span>
            </ValidationProvider>
            <div class="br_iface_info">
                Interface info: {{ iface_desc }}
            </div>
        </div>

        <v-btn @click="start_graph()" :disabled="invalid">
            Start graphs
        </v-btn>
        <v-btn @click="stop_graph()" :disabled="!running">
            Stop graphs
        </v-btn>

        <v-alert
            v-if="error_message"
            class="mt-4"
            type="error"
            outlined
        >
            {{ error_message }}
        </v-alert>

        <div class="br_before_iface_graphs"></div>
        <section class="br_iface_graph_section">
            <h3 class="br">Interface traffic, bit/s</h3>
            <div ref="traffic_chart" class="br_iface_graph"></div>
        </section>

        <section class="br_iface_graph_section">
            <h3 class="br">Interface packets, pkts/sec</h3>
            <div ref="packet_chart" class="br_iface_graph"></div>
        </section>
    </ValidationObserver>
</div>
</template>

<script>

import * as echarts from "echarts";
import "../jque.js";
import "../validation_rules.js";
import api_client from "../services/api.js";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ipoe_sub_box from "../components/ipoe_sub_box.vue";
import pppoe_sub_box from "../components/pppoe_sub_box.vue";
import vif_box from "../components/vif_box.vue";


const POLL_INTERVAL_MS = 1000;
const GRAPH_WINDOW_MS = 15 * 60 * 1000;
const MAX_SAMPLES = GRAPH_WINDOW_MS / POLL_INTERVAL_MS;
const COUNTER_FIELDS = ["rxb", "txb", "rxp", "txp"];
const COUNTER_ENDPOINTS = {
    "VIF": "/vif/counters",
    "PPPoE subscriber": "/pppoe/ipv4_subsc_with_id",
    "IPoE subscriber": "/ipoe/subsc_with_id",
};


function format_number(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 100) {
        return value.toFixed(0);
    }
    if (absolute_value >= 10) {
        return value.toFixed(1);
    }
    return value.toFixed(2);
}


function rate_unit(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 1e12) {
        return { divisor: 1e12, prefix: "T" };
    }
    if (absolute_value >= 1e9) {
        return { divisor: 1e9, prefix: "G" };
    }
    if (absolute_value >= 1e6) {
        return { divisor: 1e6, prefix: "M" };
    }
    if (absolute_value >= 1e3) {
        return { divisor: 1e3, prefix: "K" };
    }
    return { divisor: 1, prefix: "" };
}


function format_axis_rate(value) {
    const unit = rate_unit(value);
    return (
        format_number(value / unit.divisor) + " " + unit.prefix
    ).trim();
}


function format_tooltip_rate(value, suffix) {
    if (!Number.isFinite(value)) {
        return "-";
    }
    const unit = rate_unit(value);
    return (
        format_number(value / unit.divisor) + " " +
        unit.prefix + suffix
    );
}


function format_tooltip(parameters, suffix) {
    if (!parameters.length) {
        return "";
    }
    const timestamp = new Date(parameters[0].value[0]).toLocaleString();
    const values = parameters.map(parameter => (
        parameter.marker + parameter.seriesName + ": " +
        format_tooltip_rate(parameter.value[1], suffix)
    ));
    return [timestamp].concat(values).join("<br>");
}


function chart_options(y_axis_name, tooltip_suffix) {
    return {
        animation: false,
        color: ["#2878b5", "#2a9d55"],
        tooltip: {
            trigger: "axis",
            formatter: parameters => format_tooltip(
                parameters,
                tooltip_suffix,
            ),
        },
        legend: {
            data: ["RX", "TX"],
            top: 4,
        },
        grid: {
            bottom: 44,
            containLabel: true,
            left: 20,
            right: 24,
            top: 48,
        },
        xAxis: {
            boundaryGap: false,
            type: "time",
            axisLabel: {
                hideOverlap: true,
            },
        },
        yAxis: {
            min: 0,
            name: y_axis_name,
            type: "value",
            axisLabel: {
                formatter: format_axis_rate,
            },
        },
        series: [
            {
                id: "rx",
                name: "RX",
                type: "line",
                data: [],
                lineStyle: {
                    width: 2,
                },
                sampling: "lttb",
                showSymbol: false,
            },
            {
                id: "tx",
                name: "TX",
                type: "line",
                data: [],
                lineStyle: {
                    width: 2,
                },
                sampling: "lttb",
                showSymbol: false,
            },
        ],
    };
}


function parse_counter_response(data) {
    let counters = data;
    if (typeof counters === "string") {
        try {
            counters = JSON.parse(counters);
        }
        catch (error) {
            throw new Error(
                "BR command interface returned invalid JSON: " +
                error.message
            );
        }
    }

    if (!counters || typeof counters !== "object" || Array.isArray(counters)) {
        throw new Error("BR command interface returned invalid counters");
    }
    if (Object.prototype.hasOwnProperty.call(counters, "err_msg")) {
        throw new Error(
            counters.err_msg || "Failed to read interface counters"
        );
    }

    const result = {};
    COUNTER_FIELDS.forEach(field => {
        const value = counters[field];
        if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
            throw new Error("BR returned an invalid " + field + " counter");
        }
        result[field] = value;
    });
    return result;
}


function request_error_message(error) {
    const response = error.response;
    if (response && response.data && response.data.detail) {
        return response.data.detail;
    }
    return error.message || "Failed to get data from BisonRouter";
}


export default {
    name: "iface_graph",

    components: {
        ValidationObserver,
        ValidationProvider,
        ipoe_sub_box,
        pppoe_sub_box,
        vif_box,
    },

    data: () => ({
        error_message: "",
        iface_desc: "",
        iface_type: "VIF",
        poll_generation: 0,
        poll_timeout_id: null,
        running: false,
        show_ipoe_sub_form: false,
        show_pppoe_sub_form: false,
        show_vif_form: false,
        vif_id: "",
    }),

    watch: {
        iface_type() {
            this.iface_desc = "";
            this.interface_selection_changed();
        },

        vif_id() {
            this.interface_selection_changed();
        },
    },

    created() {
        // Keep high-frequency samples outside Vue's reactive data tree.
        this.samples = [];
        this.previous_counters = null;
        this.active_request = null;
        this.traffic_chart = null;
        this.packet_chart = null;
    },

    mounted() {
        this.traffic_chart = echarts.init(this.$refs.traffic_chart);
        this.packet_chart = echarts.init(this.$refs.packet_chart);
        this.traffic_chart.setOption(chart_options("bit/s", "bit/s"));
        this.packet_chart.setOption(chart_options("pkts/s", "pkts/s"));
        window.addEventListener("resize", this.resize_charts);
    },

    beforeDestroy() {
        this.stop_graph();
        window.removeEventListener("resize", this.resize_charts);
        if (this.traffic_chart) {
            this.traffic_chart.dispose();
            this.traffic_chart = null;
        }
        if (this.packet_chart) {
            this.packet_chart.dispose();
            this.packet_chart = null;
        }
    },

    beforeRouteLeave(to, from, next) {
        this.stop_graph();
        next(true);
    },

    methods: {
        interface_selection_changed() {
            this.stop_graph();
            this.error_message = "";
            this.reset_graph_data();
        },

        counter_request() {
            const endpoint = COUNTER_ENDPOINTS[this.iface_type];
            if (!endpoint) {
                throw new Error("Unknown interface type");
            }
            return {
                endpoint,
                parameters: {
                    id: this.vif_id,
                },
            };
        },

        start_graph() {
            this.stop_graph();
            this.error_message = "";
            this.reset_graph_data();

            try {
                this.active_request = this.counter_request();
            }
            catch (error) {
                this.error_message = error.message;
                return;
            }

            this.running = true;
            this.poll_graph(this.poll_generation);
        },

        stop_graph() {
            this.running = false;
            this.poll_generation += 1;
            this.active_request = null;
            if (this.poll_timeout_id !== null) {
                clearTimeout(this.poll_timeout_id);
                this.poll_timeout_id = null;
            }
        },

        poll_is_active(generation) {
            return this.running && generation === this.poll_generation;
        },

        poll_graph(generation) {
            if (!this.poll_is_active(generation) || !this.active_request) {
                return;
            }

            const request = this.active_request;
            const request_started_at = Date.now();
            api_client.run_post_query(request.endpoint, request.parameters)
            .then(response => {
                if (!this.poll_is_active(generation)) {
                    return;
                }
                const counters = parse_counter_response(response.data);
                this.error_message = "";
                this.record_counter_sample(counters, Date.now());
            })
            .catch(error => {
                if (this.poll_is_active(generation)) {
                    this.error_message = request_error_message(error);
                    console.log(
                        "failed to get data from bisonrouter: " +
                        this.error_message
                    );
                }
            })
            .finally(() => {
                if (!this.poll_is_active(generation)) {
                    return;
                }
                const request_duration = Date.now() - request_started_at;
                const delay = Math.max(0, POLL_INTERVAL_MS - request_duration);
                this.poll_timeout_id = setTimeout(() => {
                    this.poll_timeout_id = null;
                    this.poll_graph(generation);
                }, delay);
            });
        },

        record_counter_sample(counters, timestamp) {
            const previous = this.previous_counters;
            this.previous_counters = Object.assign(
                { time: timestamp },
                counters,
            );
            if (previous === null) {
                return;
            }

            const elapsed_seconds = (timestamp - previous.time) / 1000;
            const counters_reset = COUNTER_FIELDS.some(field => (
                counters[field] < previous[field]
            ));
            if (elapsed_seconds <= 0 || counters_reset) {
                return;
            }

            this.samples.push({
                time: timestamp,
                rx_bps: Math.floor(
                    (counters.rxb - previous.rxb) * 8 / elapsed_seconds
                ),
                tx_bps: Math.floor(
                    (counters.txb - previous.txb) * 8 / elapsed_seconds
                ),
                rx_pps: Math.floor(
                    (counters.rxp - previous.rxp) / elapsed_seconds
                ),
                tx_pps: Math.floor(
                    (counters.txp - previous.txp) / elapsed_seconds
                ),
            });
            this.prune_samples(timestamp);
            this.render_charts();
        },

        prune_samples(timestamp) {
            const cutoff = timestamp - GRAPH_WINDOW_MS;
            let expired_count = 0;
            while (
                expired_count < this.samples.length &&
                this.samples[expired_count].time < cutoff
            ) {
                expired_count += 1;
            }

            const overflow_count = Math.max(
                0,
                this.samples.length - MAX_SAMPLES,
            );
            const remove_count = Math.max(expired_count, overflow_count);
            if (remove_count > 0) {
                this.samples.splice(0, remove_count);
            }
        },

        reset_graph_data() {
            this.samples.length = 0;
            this.previous_counters = null;
            this.render_charts();
        },

        render_charts() {
            if (!this.traffic_chart || !this.packet_chart) {
                return;
            }

            const window_end = this.samples.length > 0
                ? this.samples[this.samples.length - 1].time
                : Date.now();
            const x_axis = {
                min: window_end - GRAPH_WINDOW_MS,
                max: window_end,
            };
            const rx_bits = [];
            const tx_bits = [];
            const rx_packets = [];
            const tx_packets = [];
            this.samples.forEach(sample => {
                rx_bits.push([sample.time, sample.rx_bps]);
                tx_bits.push([sample.time, sample.tx_bps]);
                rx_packets.push([sample.time, sample.rx_pps]);
                tx_packets.push([sample.time, sample.tx_pps]);
            });

            this.traffic_chart.setOption({
                xAxis: x_axis,
                series: [
                    { id: "rx", data: rx_bits },
                    { id: "tx", data: tx_bits },
                ],
            });
            this.packet_chart.setOption({
                xAxis: x_axis,
                series: [
                    { id: "rx", data: rx_packets },
                    { id: "tx", data: tx_packets },
                ],
            });
        },

        resize_charts() {
            if (this.traffic_chart) {
                this.traffic_chart.resize();
            }
            if (this.packet_chart) {
                this.packet_chart.resize();
            }
        },

        open_iface_form() {
            if (this.iface_type === "VIF") {
                this.open_vif_form();
            }
            else if (this.iface_type === "PPPoE subscriber") {
                this.open_pppoe_sub_form();
            }
            else if (this.iface_type === "IPoE subscriber") {
                this.open_ipoe_sub_form();
            }
        },

        open_pppoe_sub_form() {
            this.show_pppoe_sub_form = true;
        },

        on_pppoe_sub_form_cancel() {
            this.show_pppoe_sub_form = false;
        },

        on_pppoe_sub_form_select(data) {
            this.vif_id = data.id;
            this.iface_desc = "PPPoE username: " + data.username;
            this.show_pppoe_sub_form = false;
        },

        open_ipoe_sub_form() {
            this.show_ipoe_sub_form = true;
        },

        on_ipoe_sub_form_cancel() {
            this.show_ipoe_sub_form = false;
        },

        on_ipoe_sub_form_select(data) {
            this.vif_id = data.id;
            this.iface_desc = "IPoE username: " + data.username;
            this.show_ipoe_sub_form = false;
        },

        open_vif_form() {
            this.show_vif_form = true;
        },

        on_vif_form_cancel() {
            this.show_vif_form = false;
        },

        on_vif_form_select(data) {
            this.vif_id = data.id;
            this.iface_desc = "VIF name: " + data.name;
            this.show_vif_form = false;
        },
    },
};

</script>
